import { parseSignedRegistryConfig } from '../../source/shared/pilot-boundaries.js';
import { periodCatalog } from '../packages/tool-registry/seed/periods.ts';
import { historicalPeriodTools } from '../packages/tool-registry/seed/period-tools.ts';
import { resolveToolPeriod as resolvePeriod } from '../../source/shared/period-policy.ts';
import type { PeriodSelection, PeriodTool } from '../../source/shared/period-policy.ts';
import type {
  DateTimeToolId,
  EducationToolId,
  EverydayHealthToolId,
  HomeRenovationToolId,
  ToolDefinition,
  ToolId,
} from '@hephesapla/tool-registry';
import type {
  FinanceBusinessRuntimeMap,
  FinanceBusinessToolId,
} from '@hephesapla/calculation-core';
import { getBrowserBundledPilotTools } from '@hephesapla/tool-registry/browser-contract';

import {
  calculateLegacyWebFraction,
  calculateLegacyWebGcdLcm,
  calculateLegacyWebGeometry,
  calculateLegacyWebHgs,
  calculateWebDateTime,
  calculateWebEducation,
  calculateWebEverydayHealth,
  calculateWebFinanceBusiness,
  calculateWebHomeRenovation,
  calculateLegacyWebPercentage,
  calculateLegacyWebPowerRoot,
  calculateLegacyWebPrime,
  calculateLegacyWebPythagorean,
  calculateLegacyWebQuadratic,
  calculateLegacyWebRatio,
  calculateLegacyWebSalary,
  legacyHgsHasNightTariff,
  type LegacyFractionOperation,
  type LegacyGeometryShape,
  type LegacyHgsCrossing,
  type LegacyPercentageMode,
  type LegacyPowerRootMode,
  type LegacyPythagoreanMode,
  type LegacyRatioMode,
  type LegacySalaryMode,
} from './index.js';
import {
  parseBrowserToolDefinitions,
  bootstrapSignedRegistry,
  type BrowserRegistryStatus,
  type SignedRegistryBootstrapConfig,
} from './signedRegistry.js';

export {
  calculateLegacyWebFraction,
  calculateLegacyWebGcdLcm,
  calculateLegacyWebGeometry,
  calculateLegacyWebHgs,
  calculateLegacyWebPercentage,
  calculateLegacyWebPowerRoot,
  calculateLegacyWebPrime,
  calculateLegacyWebPythagorean,
  calculateLegacyWebQuadratic,
  calculateLegacyWebRatio,
  calculateLegacyWebSalary,
  calculateWebDateTime,
  calculateWebEducation,
  calculateWebEverydayHealth,
  calculateWebFinanceBusiness,
  calculateWebHomeRenovation,
  canonicalFractionOperation,
  canonicalGeometryShape,
  canonicalHgsCrossing,
  canonicalPercentageMode,
  canonicalPowerRootMode,
  canonicalPythagoreanMode,
  canonicalRatioMode,
  canonicalSalaryMode,
  legacyHgsHasNightTariff,
} from './index.js';
export { fetchSignedRegistrySnapshot } from './signedRegistry.js';

// Uzak yayın yapılandırılmasa bile katalog Exact108'in imzalı yayınla aynı
// kanonik sıra sözleşmesini taşır; bozuk paket güvenli fallback sayılmaz.
const bundledTools = parseBrowserToolDefinitions(getBrowserBundledPilotTools());
let activeTools: readonly ToolDefinition[] = bundledTools;
let activeStatus: BrowserRegistryStatus = {
  source: 'bundled-fallback',
  message: 'Uzak yayın yapılandırılmadı; güvenli paket kullanılıyor.',
  releaseId: null,
  sequence: null,
  tools: bundledTools,
};

function globalRegistryConfig(): SignedRegistryBootstrapConfig | null {
  const host: object = globalThis;
  const value = '__HEPHESAPLA_SIGNED_REGISTRY__' in host
    ? host.__HEPHESAPLA_SIGNED_REGISTRY__ : undefined;
  return parseSignedRegistryConfig(value);
}

const configuredRegistry = globalRegistryConfig();
const readyPromise = configuredRegistry
  ? bootstrapSignedRegistry(configuredRegistry, bundledTools).then((status) => {
      activeTools = status.tools;
      activeStatus = status;
      return status;
    }).catch(() => {
      activeTools = bundledTools;
      activeStatus = {
        source: 'bundled-fallback',
        message: 'Ortak yayın başlatılamadı; güvenli paket kullanılıyor.',
        releaseId: null,
        sequence: null,
        tools: bundledTools,
      };
      return activeStatus;
    })
  : Promise.resolve(activeStatus);

export function registryReady(): Promise<BrowserRegistryStatus> {
  return readyPromise;
}

export function registryStatus(): BrowserRegistryStatus {
  return activeStatus;
}

const REGISTRY_UNAVAILABLE_MESSAGE =
  'Hesaplama aracı şu anda kullanılamıyor. Lütfen daha sonra tekrar deneyin.';

function hideRegistryStatus(statusElement: HTMLElement | null, source: string): void {
  if (!statusElement) return;

  // Yayın/cache ayrıntıları kullanıcı içeriği değildir; eski bir durum öğesi varsa
  // ekran okuyucular dahil görünür akıştan çıkarılır, yalnız iç denetim özniteliği korunur.
  statusElement.textContent = '';
  statusElement.hidden = true;
  statusElement.setAttribute('aria-hidden', 'true');
  statusElement.removeAttribute('role');
  statusElement.removeAttribute('aria-live');
  statusElement.setAttribute('data-registry-source', source);
}

function showRegistryUnavailable(
  documentRef: Document | undefined,
  statusElement: HTMLElement | null,
): HTMLElement | null {
  if (!documentRef) return null;

  let userStatusElement = statusElement;
  if (!userStatusElement) {
    userStatusElement = documentRef.createElement('p');
    userStatusElement.id = 'hh-registry-status';
    const intro = documentRef.querySelector('.calc-module .calc-intro');
    if (intro?.parentNode) intro.parentNode.insertBefore(userStatusElement, intro.nextSibling);
  }

  userStatusElement.className = 'field-error is-visible';
  userStatusElement.hidden = false;
  userStatusElement.removeAttribute('aria-hidden');
  userStatusElement.setAttribute('role', 'alert');
  userStatusElement.setAttribute('aria-live', 'polite');
  userStatusElement.setAttribute('data-registry-source', 'unavailable');
  userStatusElement.textContent = REGISTRY_UNAVAILABLE_MESSAGE;
  return userStatusElement;
}

/**
 * Mevcut PHP formlarındaki hesap düğmesini registry doğrulanana kadar kapalı
 * tutar. Böylece ilk hızlı tıklama dahi eski seed ile başlayıp birkaç milisaniye
 * sonra imzalı yayına geçemez; her hesap tek ve atomik snapshot kullanır.
 */
export function bindCalculatorRegistry(
  button: HTMLButtonElement,
  onReady?: (status: BrowserRegistryStatus) => void,
): Promise<BrowserRegistryStatus> {
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  const documentRef = globalThis.document;
  let statusElement = documentRef?.getElementById('hh-registry-status') ?? null;
  hideRegistryStatus(statusElement, 'loading');
  documentRef?.documentElement.setAttribute('data-hh-registry-source', 'loading');
  documentRef?.documentElement.removeAttribute('data-hh-registry-sequence');

  return registryReady().then(
    (status) => {
      documentRef?.documentElement.setAttribute('data-hh-registry-source', status.source);
      documentRef?.documentElement.setAttribute(
        'data-hh-registry-sequence',
        status.sequence === null ? 'bundled' : String(status.sequence),
      );
      hideRegistryStatus(statusElement, status.source);
      onReady?.(status);
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
      return status;
    },
    (error: unknown) => {
      // Yalnız hesap motoru gerçekten hazır olamadığında kullanıcıya sade bir mesaj verilir;
      // hata nesnesi, yayın kimliği ve cache ayrıntıları hiçbir zaman DOM'a taşınmaz.
      documentRef?.documentElement.setAttribute('data-hh-registry-source', 'unavailable');
      documentRef?.documentElement.removeAttribute('data-hh-registry-sequence');
      statusElement = showRegistryUnavailable(documentRef, statusElement);
      button.disabled = true;
      button.removeAttribute('aria-busy');
      button.setAttribute('aria-disabled', 'true');
      throw error;
    },
  );
}

/**
 * Yerel staging ve ilerideki web istemcisi aynı derleme içindeki doğrulanmış
 * registry seed'ine düşer. Burada ağdan JavaScript, formül veya config okunmaz.
 */
export function getBundledPilotTool(toolId: ToolId): ToolDefinition {
  const tool = bundledTools.find((candidate) => candidate.toolId === toolId);
  if (!tool) throw new Error(`${toolId} için paketlenmiş araç kaydı bulunamadı.`);
  return tool;
}

function toolHasId<T extends ToolId>(tool: ToolDefinition | undefined, toolId: T): tool is ToolDefinition & { readonly toolId: T } {
  return tool?.toolId === toolId;
}

export function getActivePilotTool<T extends ToolId>(toolId: T): ToolDefinition & { readonly toolId: T } {
  const tool = activeTools.find((candidate) => candidate.toolId === toolId);
  if (!toolHasId(tool, toolId)) throw new Error(`${toolId} etkin registry kaydında bulunamadı.`);
  return tool;
}

/** Europe/Istanbul takvim gününü UTC gece sınırından bağımsız üretir. */
export function currentIstanbulDate(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value;
  const year = part('year');
  const month = part('month');
  const day = part('day');
  if (!year || !month || !day) throw new Error('Europe/Istanbul tarihi üretilemedi.');
  return `${year}-${month}-${day}`;
}

export function calculateBundledPercentage(
  input: { readonly mode: LegacyPercentageMode; readonly x: number; readonly y: number },
  currentDate: string = currentIstanbulDate(),
) {
  return calculateLegacyWebPercentage(
    getActivePilotTool('percentage'),
    input,
    currentDate,
  );
}

export function calculateBundledSalary(
  input: { readonly mode: LegacySalaryMode; readonly monthlyAmount: number },
  currentDate: string = currentIstanbulDate(),
  selection?: PeriodSelection,
) {
  if (!selection) return calculateLegacyWebSalary(getActivePilotTool('salary'), input, currentDate);
  const resolved = resolveToolPeriod('salary', 'all', selection, currentDate);
  if (resolved.availability !== 'available') throw new Error(resolved.message);
  const tool = periodBoundTool('salary', resolved.snapshot.policyRevisionId);
  const result = calculateLegacyWebSalary(tool, input, currentDate, resolved.snapshot);
  return { ...result, metadata: { ...result.metadata, policySnapshot: resolved.snapshot } };
}

export function getPeriodCatalog(): readonly PeriodTool[] {
  return Object.freeze(periodCatalog.map(tool => {
    const active = activeTools.find(item => item.toolId === tool.toolId);
    if (!active || active.configVersion === tool.configVersion || !tool.scopes.some(scope => scope.coverageRule === 'none')) return tool;
    const config = active.config;
    const lifecycle = config.kind === 'pos-commission-v1' ? config.policy.policyLifecycle : 'policyLifecycle' in config ? config.policyLifecycle : { reviewedOn: active.lastReviewed, reviewAfter: active.updateCadence.reviewAfter };
    const hasBoundedScope = tool.scopes.some(scope => scope.coverageRule !== 'none');
    const activeSources = active.sources.map(source => {
      const previous = tool.sources.find(item => item.sourceId === source.sourceId);
      const sameUrl = previous?.url === source.url;
      return Object.freeze({ ...source, sourceId: hasBoundedScope && previous && !sameUrl ? `${source.sourceId}@${active.configVersion}` : source.sourceId,
        accessState: sameUrl ? previous.accessState ?? 'unchecked' : 'unchecked', accessedAt: sameUrl ? previous.accessedAt ?? null : null });
    });
    const sources = Object.freeze([...tool.sources.filter(source => !activeSources.some(item => item.sourceId === source.sourceId)), ...activeSources]);
    return Object.freeze({ ...tool, sources, scopes: Object.freeze(tool.scopes.map(scope => scope.coverageRule !== 'none' ? scope : Object.freeze({ ...scope,
      periods: Object.freeze(scope.periods.map(period => Object.freeze({ ...period, policyRevisionId: active.configVersion,
        verifiedAt: lifecycle.reviewedOn, reviewAfter: lifecycle.reviewAfter, sourceIds: Object.freeze(activeSources.map(source => source.sourceId)) }))) }))) });
  }));
}

export function resolveToolPeriod(toolId: string, modeId: string, selection: PeriodSelection, currentDate: string = currentIstanbulDate()) {
  return resolvePeriod(getPeriodCatalog(), toolId, modeId, selection, currentDate);
}

function periodBoundTool<T extends ToolId>(toolId: T, revision: string): ToolDefinition & { readonly toolId: T } {
  const historical = historicalPeriodTools.find(tool => tool.toolId === toolId && tool.configVersion === revision)
    ?? bundledTools.find(tool => tool.toolId === toolId && tool.configVersion === revision);
  if (!toolHasId(historical, toolId)) throw new Error('Seçilen dönemin veri sürümü bulunamadı.');
  return historical;
}

export function calculateBundledHgs(
  input: {
    readonly crossing: LegacyHgsCrossing;
    readonly vehicleClass: 1 | 2 | 3 | 4 | 5 | 6;
    readonly time: 'gunduz' | 'gece';
    readonly direction: 'tek' | 'cift';
  },
  currentDate: string = currentIstanbulDate(),
  selection?: PeriodSelection,
) {
  const resolved = selection ? resolveToolPeriod('hgs-toll', 'all', selection, currentDate) : null;
  if (resolved && resolved.availability !== 'available') throw new Error(resolved.message);
  const tool = resolved ? periodBoundTool('hgs-toll', resolved.snapshot.policyRevisionId) : getActivePilotTool('hgs-toll');
  const result = calculateLegacyWebHgs(tool, {
    ...input,
    currentDate,
  });
  return resolved ? { ...result, metadata: { ...result.metadata, policySnapshot: resolved.snapshot } } : result;
}

export function calculateBundledGeometry(
  input: { readonly shape: LegacyGeometryShape; readonly dimensions: Readonly<Record<string, number>> },
  currentDate: string = currentIstanbulDate(),
) {
  return calculateLegacyWebGeometry(getActivePilotTool('geometry'), input, currentDate);
}

export function calculateBundledPrime(
  input: { readonly value: string },
  currentDate: string = currentIstanbulDate(),
) {
  return calculateLegacyWebPrime(getActivePilotTool('prime-number'), input, currentDate);
}

export function calculateBundledGcdLcm(
  input: { readonly values: readonly string[] },
  currentDate: string = currentIstanbulDate(),
) {
  return calculateLegacyWebGcdLcm(getActivePilotTool('gcd-lcm'), input, currentDate);
}

export function calculateBundledPowerRoot(
  input: { readonly mode: LegacyPowerRootMode; readonly x: number; readonly n?: number },
  currentDate: string = currentIstanbulDate(),
) {
  return calculateLegacyWebPowerRoot(getActivePilotTool('power-root'), input, currentDate);
}

export function calculateBundledQuadratic(
  input: { readonly a: number; readonly b: number; readonly c: number },
  currentDate: string = currentIstanbulDate(),
) {
  return calculateLegacyWebQuadratic(getActivePilotTool('quadratic-equation'), input, currentDate);
}

export function calculateBundledFraction(
  input: {
    readonly operation: LegacyFractionOperation;
    readonly numerator1: number;
    readonly denominator1: number;
    readonly numerator2: number;
    readonly denominator2: number;
  },
  currentDate: string = currentIstanbulDate(),
) {
  return calculateLegacyWebFraction(getActivePilotTool('fraction'), input, currentDate);
}

export function calculateBundledRatio(
  input: { readonly mode: LegacyRatioMode; readonly a: number; readonly b: number; readonly c: number },
  currentDate: string = currentIstanbulDate(),
) {
  return calculateLegacyWebRatio(getActivePilotTool('ratio-proportion'), input, currentDate);
}

export function calculateBundledPythagorean(
  input: { readonly mode: LegacyPythagoreanMode; readonly a: number; readonly other: number },
  currentDate: string = currentIstanbulDate(),
) {
  return calculateLegacyWebPythagorean(getActivePilotTool('pythagorean'), input, currentDate);
}

/**
 * Ortak ev/tadilat girişinde kullanıcıdan toolId alınmaz; çağıran controller
 * yalnız sayfadaki alanları verir, güvenilir kimlik aktif registry kaydından
 * adapter katmanında eklenir.
 */
export function calculateBundledHomeRenovation(
  toolId: HomeRenovationToolId,
  userInput: Readonly<Record<string, unknown>>,
  currentDate: string = currentIstanbulDate(),
) {
  return calculateWebHomeRenovation(
    getActivePilotTool(toolId),
    userInput,
    currentDate,
  );
}

/** Tool kimliği sayfadaki kapalı route bağından gelir; kullanıcı form alanı değildir. */
export function calculateBundledDateTime(
  toolId: DateTimeToolId,
  userInput: Readonly<Record<string, unknown>>,
) {
  return calculateWebDateTime(getActivePilotTool(toolId), userInput);
}

/** İmzalı eğitim kaydı config, lifecycle ve kaynaklarıyla atomik çalıştırılır. */
export function calculateBundledEducation(
  toolId: EducationToolId,
  userInput: Readonly<Record<string, unknown>>,
  currentDate: string = currentIstanbulDate(),
) {
  return calculateWebEducation(getActivePilotTool(toolId), userInput, currentDate);
}

/** Günlük/sağlık kimliği yalnız sayfanın kapalı route bağından seçilir. */
export function calculateBundledEverydayHealth(
  toolId: EverydayHealthToolId,
  userInput: Readonly<Record<string, unknown>>,
  currentDate: string = currentIstanbulDate(),
) {
  return calculateWebEverydayHealth(getActivePilotTool(toolId), userInput, currentDate);
}

/**
 * Finans/işletme controllerları yalnız görünür form girdilerini gönderir.
 * Config, oranlar ve policy yaşam döngüsü aktif imzalı registry kaydından
 * atomik olarak çözülür; controller hiçbir politika sabiti taşımaz.
 */
export function calculateBundledFinanceBusiness<TToolId extends FinanceBusinessToolId>(
  toolId: TToolId,
  userInput: FinanceBusinessRuntimeMap[TToolId]['input'],
  currentDate: string = currentIstanbulDate(),
  selection?: PeriodSelection,
) {
  const modeId = toolId === 'depreciation-plan' && 'method' in userInput && userInput.method === 'declining-balance' ? 'declining-balance' : toolId === 'depreciation-plan' ? 'straight-line' : toolId === 'pos-commission' && 'mode' in userInput && userInput.mode === 'commission-plus-bsmv' ? 'commission-plus-bsmv' : toolId === 'pos-commission' ? 'commission-only' : 'all';
  const scope = getPeriodCatalog().find(tool => tool.toolId === toolId)?.scopes.find(scope => scope.modeId === modeId);
  const resolved = selection || scope?.coverageRule === 'none' ? resolveToolPeriod(toolId, modeId, selection ?? { kind: 'current' }, currentDate) : null;
  if (resolved && resolved.availability !== 'available') throw new Error(resolved.message);
  return calculateWebFinanceBusiness(
    resolved && scope?.coverageRule !== 'none' ? periodBoundTool(toolId, resolved.snapshot.policyRevisionId) : getActivePilotTool(toolId),
    userInput,
    currentDate,
    resolved?.snapshot,
  );
}

export function hasActiveNightTariff(
  crossing: LegacyHgsCrossing,
  currentDate: string = currentIstanbulDate(),
  selection?: PeriodSelection,
): boolean {
  const resolved = selection ? resolveToolPeriod('hgs-toll', 'all', selection, currentDate) : null;
  if (resolved && resolved.availability !== 'available') throw new Error(resolved.message);
  return legacyHgsHasNightTariff(resolved ? periodBoundTool('hgs-toll', resolved.snapshot.policyRevisionId) : getActivePilotTool('hgs-toll'), crossing, currentDate);
}

/** Staging smoke testinin hangi ortak yayın hattını çalıştırdığını görünür kılar. */
export const release = Object.freeze({
  client: '@hephesapla/web-client',
  version: '0.4.1',
  configSource: 'signed-registry-with-bundled-fallback',
});
